import React, {useEffect, useRef, useState} from 'react';
import {
  Segment,
  Form,
  Input,
  Button,
  Header,
  Icon,
  Message,
} from 'semantic-ui-react';
import OnboardingBackground from '../assets/onboarding/signup-colorful-bkg.svg';
import queryString from 'query-string';
import {History, Location} from 'history';

import Loader from './WandbLoader';
import LinkButton from './LinkButton';
import {isInJupyterNotebook, Auth} from '../setup';
import {readCachedAuthState} from '../reducers/auth';
import {displayError} from '../state/global/actions';
import {doAuthRedirect} from '../util/auth';

import {
  propagateErrorsContext,
  extractErrorMessageFromApolloError,
} from '../util/errors';
import {useResetPasswordMutation} from '../generated/graphql';
import makeComp from '../util/profiler';

interface LoginProps {
  auth: Auth;
  history: History;
  location: Location;
  currentIdToken?: string;
}

const getCachedTokenTail = () => {
  const currentCached = readCachedAuthState();

  if (currentCached.currentIdToken) {
    return currentCached.currentIdToken.slice(
      currentCached.currentIdToken.length - 4
    );
  }

  return 'null';
};

const Login: React.FC<LoginProps> = makeComp(
  ({auth, currentIdToken, history, location}) => {
    const [referrer, setReferrer] = useState<{prompt?: boolean}>({});
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState(false);
    const [showResetPassword, setShowResetPassword] = useState(false);
    const [resetPasswordError, setResetPasswordError] = useState<boolean>(
      false
    );
    const [resetPasswordSuccess, setResetPasswordSuccess] = useState<boolean>(
      false
    );

    const [resetPassword] = useResetPasswordMutation({
      context: propagateErrorsContext(),
    });

    useEffect(() => {
      // Auth0 redirects to /login#token... so we only show the modal login if the url is clean
      const authenticated = auth.authenticate();
      if (authenticated) {
        return;
      }

      // Make sure to render the loginsrv UI
      if (
        'local' in auth &&
        auth.local &&
        document.location.pathname !== '/login'
      ) {
        window.location.href = '/login';
        return;
      }

      // Jupyter / iframe mode
      if (isInJupyterNotebook()) {
        if (currentIdToken) {
          doAuthRedirect(history);
        } else {
          // Auth works funky in iframes, we attempt to reload to get creds
          setReferrer({prompt: true});
          setTimeout(() => window.location.reload(true), 10000);
        }
        return;
      }

      const options: {signup?: boolean} = {};
      const params = queryString.parse(location.search);
      if (params.signUp || params.signup) {
        options.signup = true;
      }
      // invitation links
      if (params.invited) {
        options.signup = true;
        const invited =
          typeof params.invited === 'string'
            ? params.invited
            : params.invited[0];
        localStorage.setItem('auth.invited', invited);
      }
      // localhost url used by the cli for auto login
      if (params.callback) {
        const callback =
          typeof params.callback === 'string'
            ? params.callback
            : params.callback[0];
        localStorage.setItem('auth.callback', callback);
      }

      // Show login / signup dialog
      const innerReferrer = location.state || ({} as any);
      if (innerReferrer.prompt) {
        // If we're coming from the authorize page, go straight to login and
        // skip the dialog.
        if (innerReferrer.from.pathname === '/authorize') {
          const authorizeParams = queryString.parse(innerReferrer.from.search);
          if (authorizeParams.signup) {
            options.signup = true;
          }

          auth.login(options, innerReferrer);
          return;
        }

        setReferrer(innerReferrer);
      } else if (!params.upgradeAuth && auth.loggedIn()) {
        // Already logged in, just go home. This happens when
        // people visit /site and click the login button on the
        // top-right (and are already logged in).
        history.replace('/');
      } else if (params.error && params.error === 'access_denied') {
        console.error('Access denied', params.error_description);
        auth.store!.dispatch(
          displayError({
            code: 403,
            message: (
              <>
                <p>
                  We're sorry, but there was a problem authenticating your
                  account: {params.error_description}
                </p>
                <p>
                  <Button
                    icon="refresh"
                    size="small"
                    onClick={() => auth.login(options, innerReferrer)}
                  />{' '}
                  Please click to try again.
                </p>
                <p>
                  If you continue to see this message, please email{' '}
                  <a href="mailto:support@wandb.com">support@wandb.com</a>.
                </p>
              </>
            ),
          })
        );
      } else {
        auth.login(options, innerReferrer);
      }
      // eslint-disable-next-line
    }, []);

    const form = useRef<HTMLFormElement>(null);

    // Currently only used for wandb local in a dev environment
    if ('local' in auth && auth.local) {
      return (
        <div
          className="onboarding-flow"
          style={{backgroundImage: `url(${OnboardingBackground})`}}>
          <div className="onboarding-dialogue">
            <h2>{showResetPassword ? 'Reset Password' : 'Login'}</h2>

            <div>
              <Form
                ref={form}
                onSubmit={async () => {
                  if (showResetPassword) {
                    setResetPasswordSuccess(false);
                    setResetPasswordError(false);
                    console.log(`Resetting password for ${email}`);
                    try {
                      await resetPassword({variables: {email}});
                      setResetPasswordSuccess(true);
                    } catch (e) {
                      const extracted = extractErrorMessageFromApolloError(e);
                      console.error(
                        `Error requesting password reset: ${extracted}`
                      );
                      setResetPasswordError(true);
                    }
                    return;
                  }
                  setError(false);
                  console.log(`Logging in as ${email}`);

                  console.log(
                    `Prior to login, token is ${getCachedTokenTail()}`
                  );
                  const success = await auth.remoteLogin(email, password);
                  if (success) {
                    const url = doAuthRedirect();
                    console.log(`Login successful; redirecting to ${url}`);
                    console.log(
                      `At time of redirect, token is ${getCachedTokenTail()}`
                    );
                  } else {
                    console.error(`Login failed.`);
                    console.error(
                      `After login failure, token is ${getCachedTokenTail()}`
                    );
                    setError(true);
                  }
                }}>
                <Message
                  success
                  visible={resetPasswordSuccess}
                  content="Password reset email sent"
                />
                <Form.Input
                  label="Email"
                  name="email"
                  data-test="email"
                  error={
                    resetPasswordError && 'Error while trying to reset password'
                  }
                  onChange={(e, {value}) => setEmail(value)}
                />
                {!showResetPassword && (
                  <Form.Field error={error}>
                    <label>Password</label>
                    <Input
                      type="password"
                      name="password"
                      data-test="password"
                      onChange={(e, {value}) => setPassword(value)}
                    />
                    <div
                      style={{display: error ? 'inline-block' : 'none'}}
                      role="alert"
                      className="ui pointing above prompt label">
                      Invalid password
                    </div>
                  </Form.Field>
                )}
                {!showResetPassword && (
                  <span style={{display: 'block', textAlign: 'right'}}>
                    <LinkButton
                      data-test="forgot-your-password"
                      onClick={() => {
                        setShowResetPassword(true);
                        setResetPasswordError(false);
                        setResetPasswordSuccess(false);
                        setError(false);
                      }}>
                      Forgot your password?
                    </LinkButton>
                  </span>
                )}
                <Button type="submit" style={{display: 'none'}}>
                  Login
                </Button>
              </Form>
            </div>
            <div className="actions">
              {showResetPassword && (
                <Button
                  onClick={() => {
                    setShowResetPassword(false);
                    setResetPasswordError(false);
                    setResetPasswordSuccess(false);
                    setError(false);
                  }}>
                  Cancel
                </Button>
              )}
              <Button
                primary
                data-test="submit"
                onClick={e => {
                  form.current?.handleSubmit(e);
                }}>
                {showResetPassword ? 'Reset Password' : 'Login'}
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return referrer.prompt ? (
      <div>
        <Segment
          compact
          style={{
            margin: 'auto',
            marginTop: 20,
            maxWidth: 380,
            display: 'flex',
            justifyContent: 'center',
            flexDirection: 'column',
            padding: '30px 0',
          }}>
          <Header icon style={{color: '#444'}}>
            <Icon name="user" />
            Log in or signup to view this page.
          </Header>
          <div style={{alignSelf: 'center'}}>
            <Button
              color="yellow"
              style={{color: '#444'}}
              onClick={() => {
                auth.login({}, referrer);
              }}>
              Log In
            </Button>
            <Button
              onClick={() => {
                auth.login({signup: true}, referrer);
              }}>
              Signup
            </Button>
          </div>
        </Segment>
      </div>
    ) : (
      <Loader />
    );
  },
  {id: 'Login'}
);

export default Login;
